import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CatalogueService, Frequency } from '@/components/PlanControls';
import { FrequencyPicker, RiskPicker, ServicePicker } from '@/components/PlanControls';
import type { TimeBlock } from '@/components/WeekGrid';
import { WeekGrid } from '@/components/WeekGrid';
import { describeBlocks, describePlanItem } from '@/lib/describePlan';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import { useSession } from '@/api/session';
import type { ChangeAnalysis, ClientRecord, PlanItem, PlanSections } from '@/api/types';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Tag,
  Segmented,
  TextArea,
  TextInput,
} from '@/components/ui';

/**
 * ADM-02. Author a plan from structured sections, preview it per audience, and
 * publish it as the one current version.
 *
 * The specification is explicit that these are structured fields with optional
 * instructions, "not one long text box" — so each item is a small form, and
 * visibility is a stored choice on every item rather than something inferred
 * from where it happens to render.
 */

const SECTIONS: { key: string; label: string; blurb: string; fields: FieldSpec[] }[] = [
  {
    key: 'goals',
    label: 'Goals',
    blurb: 'Safety, independence, routines',
    fields: [{ name: 'name', label: 'Goal' }, { name: 'detail', label: 'Detail', multiline: true }],
  },
  {
    key: 'tasks',
    label: 'Tasks',
    blurb: 'Frequency, instructions, limits',
    fields: [
      { name: 'service_id', label: 'Service', kind: 'service' },
      { name: 'name', label: 'Task' },
      // Replaces a free-text box and a separate day list. Days now belong to
      // the frequency, which is the only place that should know them.
      { name: 'frequency', label: 'Frequency', kind: 'frequency' },
      { name: 'instructions', label: 'Instructions', multiline: true },
      { name: 'restriction', label: 'Restriction', placeholder: 'e.g. reminder only' },
    ],
  },
  {
    key: 'preferences',
    label: 'Preferences',
    blurb: 'Name, language, routines',
    fields: [{ name: 'name', label: 'Preference' }, { name: 'detail', label: 'Detail' }],
  },
  {
    key: 'risks',
    label: 'Risks',
    blurb: 'Falls, skin, wandering, allergies',
    fields: [
      { name: 'risk_id', label: 'Risk', kind: 'risk' },
      // The category is chosen; this is where the specifics go, because
      // "keeps the landing light on" is what a standard list cannot hold.
      { name: 'detail', label: 'Detail', multiline: true },
    ],
  },
  {
    key: 'contacts',
    label: 'Contacts',
    blurb: 'Agency, family, emergency',
    fields: [
      { name: 'name', label: 'Name' },
      { name: 'phone', label: 'Phone' },
      { name: 'relationship', label: 'Relationship' },
    ],
  },
  {
    key: 'schedule',
    label: 'Default schedule',
    blurb: 'The week tasks follow unless they set their own',
    fields: [],
  },
];

type FieldSpec = {
  name: string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  list?: boolean;
  /** Fields that are a choice rather than typing. */
  kind?: 'service' | 'frequency' | 'risk';
};

const VISIBILITY_OPTIONS = [
  { value: 'family_visible' as const, label: 'Family visible' },
  { value: 'caregiver_only' as const, label: 'Caregiver only' },
  { value: 'restricted' as const, label: 'Restricted' },
];

function emptySections(): PlanSections {
  return Object.fromEntries(SECTIONS.map((section) => [section.key, []]));
}

function newId(): string {
  return `item-${Math.random().toString(36).slice(2, 9)}`;
}

export function PlanEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { recentAuth } = useSession();
  const record = useApi<ClientRecord>(`/admin/clients/${id}`);

  const catalogue = useApi<{ services: CatalogueService[] }>('/admin/services');
  const services = useMemo(() => catalogue.data?.services ?? [], [catalogue.data]);
  const riskCatalogue = useApi<{ risks: CatalogueService[] }>('/admin/risks');
  const risks = useMemo(() => riskCatalogue.data?.risks ?? [], [riskCatalogue.data]);

  const [sections, setSections] = useState<PlanSections>(emptySections);
  const [changeReason, setChangeReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  // Empty means ongoing, which is what most home care is. A date means an
  // authorisation, a post-discharge period, or a respite block that stops.
  const [endDate, setEndDate] = useState('');

  // The plan's default week. A task with no blocks of its own follows it, so
  // most plans are drawn once here rather than six times on six tasks.
  const defaultBlocks: TimeBlock[] = useMemo(() => {
    const entry = (sections.schedule ?? [])[0];
    return (entry?.blocks as TimeBlock[] | undefined) ?? [];
  }, [sections.schedule]);

  function setDefaultBlocks(next: TimeBlock[]) {
    setSections((previous) => {
      const [existing] = previous.schedule ?? [];
      return {
        ...previous,
        schedule: [
          {
            ...(existing ?? { id: newId(), visibility: 'family_visible' }),
            blocks: next,
          },
        ],
      };
    });
  }
  const [analysis, setAnalysis] = useState<ChangeAnalysis | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // The payload as the server last accepted it. Comparing against this is the
  // only honest answer to "has the server seen this?" — the question whose
  // absence let Publish send a draft the coordinator had already edited past.
  const savedPayloadRef = useRef<string | null>(null);
  // The payload whose save failed. Without this, a failing save leaves the
  // editor dirty, which re-arms autosave, which fails — a retry loop against a
  // server that is already unhappy. Editing anything clears it, because the
  // next attempt is then a different request.
  const failedPayloadRef = useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Held in state rather than read from `record`, because saving a draft for
  // the first time creates one — and without this, Preview and Publish stayed
  // disabled until the page was reloaded.
  const [draftId, setDraftId] = useState<string | null>(null);
  const loadedRef = useRef(false);

  // Seed the editor from the open draft if there is one, otherwise from the
  // published plan — editing a plan almost always starts from what is current.
  useEffect(() => {
    if (loadedRef.current || !record.data) return;
    loadedRef.current = true;
    setDraftId(record.data.draft_plan?.id ?? null);
    const source = record.data.draft_plan ?? record.data.current_plan;
    const loadedSections = source ? { ...emptySections(), ...source.sections } : emptySections();
    const loadedReason = record.data.draft_plan?.change_reason ?? '';
    const loadedDate = source?.effective_date ?? effectiveDate;
    const loadedEnd = source?.end_date ?? '';
    setSections(loadedSections);
    setChangeReason(loadedReason);
    setEffectiveDate(loadedDate);
    setEndDate(loadedEnd);

    // The baseline autosave compares against. Set here so loading a plan does
    // not immediately look like an edit.
    savedPayloadRef.current = JSON.stringify({
      ...loadedSections,
      change_reason: loadedReason.trim() || null,
      effective_date: loadedDate,
      end_date: loadedEnd || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.data]);

  const payload = useMemo(
    () => ({
      ...sections,
      change_reason: changeReason.trim() || null,
      effective_date: effectiveDate,
      // Part of every save, not something supplied only at publication — it was
      // possible to set this, save, reload, and find it gone.
      end_date: endDate || null,
    }),
    [sections, changeReason, effectiveDate, endDate],
  );
  const serialised = useMemo(() => JSON.stringify(payload), [payload]);
  const dirty = savedPayloadRef.current !== null && savedPayloadRef.current !== serialised;

  const save = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    setProblem(null);
    try {
      // Captured before the request, not after: an edit made while it is in
      // flight must still count as unsaved, or autosave would stop and the
      // coordinator would be told their work was safe when it was not.
      const sent = serialised;
      const response = await api.post<{ draft: { id: string }; change_analysis: ChangeAnalysis }>(
        `/admin/clients/${id}/plan/draft`,
        payload,
      );
      savedPayloadRef.current = sent;
      failedPayloadRef.current = null;
      setAnalysis(response.change_analysis);
      setDraftId(response.draft.id);
      setSavedAt(new Date().toISOString());
    } catch (caught) {
      failedPayloadRef.current = serialised;
      setProblem((caught as ApiError).message);
    } finally {
      setSaving(false);
    }
  }, [id, payload, serialised]);

  // Autosave. The editor has no other answer to someone closing a tab mid-plan,
  // and the gap between what was typed and what was stored is where the publish
  // bug lived.
  //
  // Only after the first load has settled: seeding the form from the current
  // plan is not an edit, and saving it would create a draft nobody asked for.
  useEffect(() => {
    if (savedPayloadRef.current === null || !dirty || saving || publishing) return;
    // Do not keep throwing the same rejected payload at the server. Pressing
    // Save is still available, and changing anything makes it a new request.
    if (failedPayloadRef.current === serialised) return;
    const timer = setTimeout(() => {
      void save();
    }, 1500);
    return () => clearTimeout(timer);
  }, [dirty, saving, publishing, save, serialised]);

  // Covers closing the tab or reloading, which autosave's debounce cannot. The
  // browser shows its own wording; the string only has to be non-empty.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  async function publish() {
    setPublishing(true);
    setProblems([]);
    setProblem(null);
    try {
      // Save first, always. Publishing used to post the *stored* draft while
      // the coordinator's edits sat in React state, so adding a goal and a task
      // and pressing Publish reported that the plan had neither — the work was
      // on screen and the server had never seen it.
      const saved = await api.post<{ draft: { id: string } }>(
        `/admin/clients/${id}/plan/draft`,
        { ...sections, change_reason: changeReason.trim() || null, effective_date: effectiveDate },
      );
      setDraftId(saved.draft.id);
      setSavedAt(new Date().toISOString());

      await api.post(`/admin/plans/${saved.draft.id}/publish`, {
        change_reason: changeReason.trim(),
        effective_date: effectiveDate,
        end_date: endDate || null,
      });
      navigate(`/clients/${id}`);
    } catch (caught) {
      const error = caught as ApiError;
      if (error.needsReauth) {
        setProblem(
          'Publishing requires a recent sign-in. Sign out and back in, then publish again.',
        );
      } else if (Array.isArray(error.details.problems)) {
        setProblems(error.details.problems as string[]);
      } else {
        setProblem(error.message);
      }
      setConfirming(false);
    } finally {
      setPublishing(false);
    }
  }

  const counts = useMemo(
    () =>
      Object.fromEntries(
        SECTIONS.map((section) => [section.key, (sections[section.key] ?? []).length]),
      ),
    [sections],
  );

  if (record.loading) return <Loading />;
  if (record.error)
    return <Page><ErrorState message={record.error.message} onRetry={record.reload} /></Page>;
  if (!record.data) return null;

  const client = record.data.client;

  function updateItem(sectionKey: string, index: number, patch: Partial<PlanItem>) {
    setSections((previous) => {
      const items = [...(previous[sectionKey] ?? [])];
      items[index] = { ...items[index], ...patch };
      return { ...previous, [sectionKey]: items };
    });
  }

  function addItem(sectionKey: string) {
    setSections((previous) => ({
      ...previous,
      [sectionKey]: [
        ...(previous[sectionKey] ?? []),
        {
          id: newId(),
          name: '',
          // Risks default to caregiver-only. Defaults here fail closed because
          // the cost of the two mistakes is not symmetric.
          visibility: sectionKey === 'risks' ? 'caregiver_only' : 'family_visible',
        },
      ],
    }));
  }

  function removeItem(sectionKey: string, index: number) {
    setSections((previous) => ({
      ...previous,
      [sectionKey]: (previous[sectionKey] ?? []).filter((_, i) => i !== index),
    }));
  }

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/clients">Clients</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/clients/${id}`}>{client.preferred_name ?? client.full_name}</Link>
            <span aria-hidden="true">/</span>
            <span>Care plan</span>
          </nav>
        }
        title="Care plan"
        subtitle={
          record.data.draft_plan
            ? `Editing draft v${record.data.draft_plan.version_number}`
            : record.data.current_plan
              ? `Based on published v${record.data.current_plan.version_number}`
              : 'Creating the first plan'
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setPreviewing(true)} disabled={!draftId}>
              Preview by audience
            </Button>
            <Button variant="secondary" onClick={save} busy={saving}>
              Save draft
            </Button>
            <Button onClick={() => setConfirming(true)} disabled={!changeReason.trim()}>
              Publish
            </Button>
          </>
        }
      />

      <div className="stack">
        {problem ? (
          <Notice tone="urgent" title="Could not continue">
            {problem}
          </Notice>
        ) : null}

        {problems.length > 0 ? (
          <Notice tone="urgent" title="This plan is not ready to publish">
            {problems.map((entry) => `• ${entry}`).join('\n')}
          </Notice>
        ) : null}

        {analysis ? (
          <Notice tone={analysis.is_material ? 'attention' : 'info'} title={analysis.summary}>
            {analysis.is_material
              ? 'A material change notifies assigned caregivers, who must acknowledge it before their next visit.\n\n'
              : 'A minor correction records a revision without requiring acknowledgment.\n\n'}
            {analysis.details.map((detail) => `• ${detail}`).join('\n') || 'No item-level changes.'}
          </Notice>
        ) : null}

        {/* Say which of the three states this is, rather than leaving the
            coordinator to guess whether their work is anywhere but the screen. */}
        <p className="meta savestate" data-state={saving ? 'saving' : dirty ? 'dirty' : savedAt ? 'saved' : 'idle'}>
          {saving
            ? 'Saving…'
            : dirty
              ? 'Unsaved changes — saving shortly'
              : savedAt
                ? `Draft saved ${new Date(savedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}. Publishing is a separate, deliberate step.`
                : 'Changes save automatically.'}
        </p>

        {SECTIONS.map((section) => (
          <Card key={section.key} variant="flush">
            <CardHead
              title={
                <div>
                  <h3>{section.label}</h3>
                  <p className="meta">{section.blurb}</p>
                </div>
              }
              actions={
                <div className="row">
                  <span className="meta">{counts[section.key]} items</span>
                  {section.key === 'schedule' ? null : (
                    <Button variant="secondary" onClick={() => addItem(section.key)}>
                      Add
                    </Button>
                  )}
                </div>
              }
            />
            <div className="card__body stack">
              {section.key === 'schedule' ? (
                <div className="stack stack--tight">
                  <p className="meta">
                    The pattern of care this plan authorises. Tasks follow it unless
                    they draw their own. It is not a roster — actual visit times live
                    on the visits themselves.
                  </p>
                  <WeekGrid blocks={defaultBlocks} onChange={setDefaultBlocks} />
                  <p className="meta">{describeBlocks(defaultBlocks)}</p>
                </div>
                            ) : (sections[section.key] ?? []).length === 0 ? (
                <p className="meta">Nothing recorded in this section yet.</p>
              ) : (
                (sections[section.key] ?? []).map((item, index) => (
                  <ItemEditor
                    key={item.id ?? index}
                    item={item}
                    spec={section.fields}
                    sectionKey={section.key}
                    services={services}
                    risks={risks}
                    inheritedBlocks={defaultBlocks}
                    onChange={(patch) => updateItem(section.key, index, patch)}
                    onRemove={() => removeItem(section.key, index)}
                  />
                ))
              )}
            </div>
          </Card>
        ))}

        <Card>
          <CardHead title="Before publishing" />
          <div className="card__body stack">
            <div className="grid-2">
              <Field label="Effective date">
                {(props) => (
                  <TextInput
                    {...props}
                    type="date"
                    value={effectiveDate}
                    onChange={(event) => setEffectiveDate(event.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Care ends"
                hint="Leave empty for ongoing care. Set it when the authorisation stops."
                error={endDate && endDate < effectiveDate ? 'Before the effective date' : null}
              >
                {(props) => (
                  <TextInput
                    {...props}
                    type="date"
                    value={endDate}
                    min={effectiveDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <Field
              label="Reason for change"
              hint="Required. Recorded permanently in the version history."
              error={changeReason.trim() ? null : 'A reason is required before publishing'}
            >
              {(props) => (
                <TextArea
                  {...props}
                  value={changeReason}
                  onChange={(event) => setChangeReason(event.target.value)}
                  placeholder="What changed and why"
                />
              )}
            </Field>
          </div>
        </Card>
      </div>

      {previewing && draftId ? (
        <AudiencePreview planId={draftId} onClose={() => setPreviewing(false)} />
      ) : null}

      {confirming ? (
        <Modal title="Publish this care plan?" onClose={() => setConfirming(false)}>
          <div className="stack">
            <p>
              This becomes the one current version for the care team. The previous version becomes
              read-only history.
            </p>
            {analysis === null ? (
              <Notice tone="info" title="Material changes require acknowledgment">
                If this changes a task, frequency, restriction, risk, or authorised service,
                assigned caregivers are notified and must acknowledge it before their next visit.
                Save the draft first to see exactly what changed.
              </Notice>
            ) : analysis.is_material ? (
              <Notice tone="attention" title="This is a material change">
                {analysis.summary}. Assigned caregivers are notified and must acknowledge it
                before their next applicable visit.
              </Notice>
            ) : (
              <Notice tone="info" title="This is a minor correction">
                {analysis.summary}. A revision is recorded; no acknowledgment is required.
              </Notice>
            )}
            {!recentAuth ? (
              <Notice tone="info" title="You may be asked to sign in again">
                Publishing is a risky action and requires a recent sign-in.
              </Notice>
            ) : null}
            <div className="row row--between">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button onClick={publish} busy={publishing}>
                Publish now
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </Page>
  );
}

function ItemEditor({
  item,
  spec,
  sectionKey,
  services,
  risks,
  inheritedBlocks,
  onChange,
  onRemove,
}: {
  item: PlanItem;
  spec: FieldSpec[];
  sectionKey: string;
  services: CatalogueService[];
  risks: CatalogueService[];
  inheritedBlocks: TimeBlock[];
  onChange: (patch: Partial<PlanItem>) => void;
  onRemove: () => void;
}) {
  const visibility = (item.visibility ?? 'family_visible') as
    | 'family_visible'
    | 'caregiver_only'
    | 'restricted';

  return (
    <div className="card" style={{ background: 'var(--soft)' }}>
      <div className="stack stack--tight">
        {spec.map((field) => {
          if (field.kind === 'service') {
            return (
              <ServicePicker
                key={field.name}
                value={item.service_id as string | undefined}
                services={services}
                onChange={(serviceId) => {
                  const chosen = services.find((s) => s.id === serviceId);
                  onChange({
                    service_id: serviceId,
                    // The name is snapshotted alongside the id because a
                    // published plan is a record of what was agreed. Renaming a
                    // catalogue entry must not rewrite plans that cite it.
                    service_name: chosen?.name ?? null,
                  });
                }}
              />
            );
          }
          if (field.kind === 'risk') {
            return (
              <RiskPicker
                key={field.name}
                value={item.risk_id as string | undefined}
                risks={risks}
                onChange={(riskId, name) =>
                  // The name is snapshotted alongside the id for the same
                  // reason a task snapshots its service: a published plan is a
                  // record of what was agreed, and renaming a catalogue entry
                  // must not rewrite it.
                  onChange({ risk_id: riskId, name: name ?? undefined })
                }
              />
            );
          }
          if (field.kind === 'frequency') {
            return (
              <FrequencyPicker
                key={field.name}
                value={item.frequency as Frequency | string | undefined}
                inherited={inheritedBlocks}
                onChange={(next) => onChange({ frequency: next, days: undefined })}
              />
            );
          }

          const raw = item[field.name];
          const value = field.list
            ? Array.isArray(raw)
              ? (raw as string[]).join(', ')
              : ''
            : typeof raw === 'string'
              ? raw
              : '';

          const handle = (next: string) =>
            onChange({
              [field.name]: field.list
                ? next
                    .split(',')
                    .map((part) => part.trim().toLowerCase())
                    .filter(Boolean)
                : next,
            });

          return (
            <Field key={field.name} label={field.label}>
              {(props) =>
                field.multiline ? (
                  <TextArea
                    {...props}
                    value={value}
                    onChange={(event) => handle(event.target.value)}
                    placeholder={field.placeholder}
                    style={{ minHeight: 64 }}
                  />
                ) : (
                  <TextInput
                    {...props}
                    value={value}
                    onChange={(event) => handle(event.target.value)}
                    placeholder={field.placeholder}
                  />
                )
              }
            </Field>
          );
        })}

        <div className="row row--between row--wrap" style={{ marginTop: 'var(--space-2)' }}>
          <div>
            <span className="field__label" style={{ display: 'block', marginBottom: 4 }}>
              Who can see this
            </span>
            <Segmented
              label="Visibility"
              options={VISIBILITY_OPTIONS}
              value={visibility}
              onChange={(next) => onChange({ visibility: next })}
            />
            {sectionKey === 'risks' && visibility === 'family_visible' ? (
              <p className="field__hint" style={{ marginTop: 4 }}>
                Risks are excluded from the family view regardless of this setting.
              </p>
            ) : null}
          </div>
          <Button variant="urgent" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The preview uses the same projection endpoint that serves the live caregiver
 * and family views, so what an administrator approves here cannot disagree with
 * what each audience actually receives.
 */
function AudiencePreview({ planId, onClose }: { planId: string; onClose: () => void }) {
  const [audience, setAudience] = useState<'caregiver' | 'family'>('family');
  const preview = useApi<{
    sections: PlanSections;
    hidden_counts: Record<string, number>;
  }>(`/admin/plans/${planId}/preview?audience=${audience}`);

  return (
    <Modal title="Audience preview" onClose={onClose} wide>
      <div className="stack">
        <Segmented
          label="Audience"
          options={[
            { value: 'family' as const, label: 'Family view' },
            { value: 'caregiver' as const, label: 'Caregiver view' },
          ]}
          value={audience}
          onChange={setAudience}
        />

        <p className="meta">
          This is generated by the same code that serves the live view, not a mock-up of it.
        </p>

        {preview.loading ? <Loading /> : null}

        {preview.data
          ? SECTIONS.map((section) => {
              const items = preview.data!.sections[section.key] ?? [];
              const hidden = preview.data!.hidden_counts[section.key] ?? 0;
              return (
                <Card key={section.key}>
                  <div className="row row--between">
                    <h3>{section.label}</h3>
                    {hidden > 0 ? (
                      <Tag label={`${hidden} hidden`} tone="neutral" />
                    ) : items.length > 0 ? (
                      <Tag label="All visible" tone="positive" />
                    ) : null}
                  </div>
                  {items.length === 0 ? (
                    <p className="meta">
                      {hidden > 0
                        ? `Not shown to the ${audience}.`
                        : 'Nothing recorded.'}
                    </p>
                  ) : (
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: '1.1rem' }}>
                      {items.map((item, index) => {
                        const { title, note } = describePlanItem(section.key, item);
                        return (
                          <li key={item.id ?? index}>
                            {title}
                            {note ? <span className="meta"> — {note}</span> : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Card>
              );
            })
          : null}

        <Button onClick={onClose} block>
          Close preview
        </Button>
      </div>
    </Modal>
  );
}
